/*
 * 应用外壳。
 *
 * 桌面端不做双栏：整体按手机版式居中，外圈铺同一张背景图放大模糊。
 * 这是旧产品横屏时的做法，也是这次产品明确要的排版。
 *
 * 右上角一排图标从旧版 CharacterChat 的 iconList 延伸而来：回忆 / 收藏 /
 * 对话图库 / 模式切换 / 我的。其中**模式切换**是旧版就有的功能（`interactionMode`，
 * user.ts:158 默认 true），在气泡模式与视觉小说模式之间切。
 * 会话历史与设置从「我的」进，不挤进这一排——旧版这排就只有四个。
 *
 * 顶部两块（数值面板、这排图标）都会**整块消失**：没有任何数值就不画面板，
 * 取不到用户信息就不画图标排。理由见各自的判据处。
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import {
  NavLink,
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
  useOutletContext,
} from "react-router-dom";
import {
  DreamBackdrop,
  Stage,
  StagePortrait,
} from "./components/DreamStage";
import {
  IconCalendar,
  IconComment,
  IconHeartOutline,
  IconImage,
  IconSolution,
  IconUser,
} from "./components/Icons";
import { useFloatingToast } from "./components/FloatingToast";
import { Sheet } from "./components/ui";
import { PowerTopupSheet } from "./components/PowerTopupSheet";
import { ValueHud, type RelationSnapshot } from "./components/ValueHud";
import { TodayChannelBubbles } from "./features/channel/TodayChannelBubbles";
import { MusicPlayerPopover } from "./components/MusicPlayer";
import { fetchRelationship, type RelationshipView } from "./services/me-api";
import { RELATIONSHIP_CHANGED_EVENT } from "./services/relationship-events";
import { LOCAL_ASSETS } from "./services/offline-preview";
import { setShareCard } from "./services/wechat-jsapi";
import { useResource, type Resource } from "./state/resource";
import { useAppearance, useSession } from "./state/session";

/* -------------------------------------------------------------------------- */
/* 登录门禁                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * 未登录一律送到 `/login`。
 *
 * 三条判断，顺序不能换：
 *
 *   checking       还没问出结果。画一层与首屏加载画面同色的底，不闪不跳。
 *                  **不能**在这里当作未登录跳登录页——已登录的用户每次刷新
 *                  都会先看到一下登录页再被弹回来。
 *   anonymous      明确未登录。
 *   blocked        账号被停用。同样送到登录页，由那里给出原因，
 *                  而不是让他停在一个功能全是横杠的界面上猜。
 *
 * 其余状态（authenticated / needs_profile / authenticating / error）都放行：
 * `session.degraded` 已经承担了「连不上但可能仍登录着」的表达，
 * 网络抖动不该把人踢出去。
 *
 * ⚠ **`maintenance` 到不了这里**，也不该在这里加一支。它描述的是入口不是这个人，
 * 与登录状态正交，判定在 `main.tsx` 的 `MaintenanceGate`——那一层在
 * `RouterProvider` 外面，因此连 `/login`（本组件管不到的那条路由）也一起覆盖。
 * 在这里再判一次的结果是未登录的人仍然会先被送去登录页。
 *
 * ## 不带 redirect
 *
 * 这里曾经拼 `/login?redirect=<当前路径>`，登录后送回原页面。已去掉：
 * **登录后一律回首页。** 触发跳转最多的场景是用户自己点「退出登录」——
 * 那时他停在 `/settings` 或 `/profile`，于是重新登录后落在设置页上，
 * 而不是缈缈面前。刚登录进来第一眼该看到的是她，不是自己上次关掉的那个抽屉。
 *
 * 代价是深链接（分享一条 `/memories` 给未登录的人）会丢失目标页。
 * 当前没有站外分享入口，三个宿主的入口地址都是 `/`，所以这个代价是零。
 * 真要做深链接时再加回来，但要把「主动退出」与「被踢出/深链接」分开处理。
 */
export function RequireSession({ children }: { children: ReactNode }) {
  const { status } = useSession();

  if (status === "checking") {
    return <div className="app-boot" aria-busy="true" aria-label="正在连接灵境" />;
  }

  if (status === "anonymous" || status === "blocked") {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

/* -------------------------------------------------------------------------- */
/* 对话模式                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * 对话模式。
 *
 *   theater  视觉小说模式：底部一个对话框，只显示最新一句 + 文字回复按钮
 *   bubble   气泡模式：完整消息列表
 *
 * 旧版默认 theater（`interactionMode: true`）。这是纯界面偏好，存本机即可，
 * 不上传、不随账号同步。
 */
export type ChatMode = "theater" | "bubble";

const MODE_KEY = "miao.pref.chatMode";

/**
 * 传给子路由的外壳上下文。
 *
 * 关系数值放在这里而不是各页自己取：左上角的 HUD 每一页都要画，
 * `/relationship` 又要展示同一份数据的完整版。各取各的会在进入关系页时
 * 打两次同样的请求，而且两个副本可能一个成功一个失败，
 * 于是面板显示数字、页面显示横杠。
 */
export interface ChatModeContext {
  mode: ChatMode;
  relationship: Resource<RelationshipView>;
  /** AVG 舞台由对话页按服务端裁定的当前形态覆盖立绘。 */
  setStagePortrait: (portrait: StagePortraitOverride | null) => void;
  /** 已到账、等待角色通过专用接口致谢的订单；数组保证连续购买也不会覆盖前一笔。 */
  pendingTopupThankOrderIds: readonly string[];
  completeTopupThank: (orderId: string) => void;
}

export interface StagePortraitOverride {
  name: string;
  portraitUrl: string | null;
  /** 只有普通形态可以回退到本地普通立绘；weakened 绝不能跨形态借图。 */
  allowLocalFallback: boolean;
}

export function useChatMode(): { mode: ChatMode } {
  return useOutletContext<ChatModeContext>();
}

/** 关系数值。由外壳统一取一次，`/relationship` 直接复用，不重复请求。 */
export function useRelationship(): Resource<RelationshipView> {
  return useOutletContext<ChatModeContext>().relationship;
}

/** 仅供对话页按当前活动形态控制外壳中的 AVG 立绘。 */
export function useStagePortrait(): ChatModeContext["setStagePortrait"] {
  return useOutletContext<ChatModeContext>().setStagePortrait;
}

/** 支付到账与聊天页之间的瞬时交接；订单事实仍以服务端为准。 */
export function useTopupThank(): Pick<
  ChatModeContext,
  "pendingTopupThankOrderIds" | "completeTopupThank"
> {
  const { pendingTopupThankOrderIds, completeTopupThank } =
    useOutletContext<ChatModeContext>();
  return { pendingTopupThankOrderIds, completeTopupThank };
}

/* -------------------------------------------------------------------------- */

/*
 * 右上角那一排的前两个入口沿用旧版 `CharacterChat.tsx` 的 iconList：
 * 日历打开「回忆点滴」（按天倒序的日记时间线），爱心打开「收藏消息」
 * （在聊天里点赞过的角色台词）。
 * 第三个图像图标是对话图库，收双方实际发送过的图片。
 *
 * ⚠ 爱心**不是**羁绊数值页。羁绊等级、好感度、灵力在旧版属于左上角那块
 * 数值面板（`MyValues` = 等级 + 好感 + 灵力），新版对应 `ValueHud`，
 * 完整版在 `/relationship`，从「我的」进。这里曾经把爱心指到 `/relationship`，
 * 是错的，已改回。
 */
const RAIL = [
  { to: "/memories", label: "回忆点滴", icon: IconCalendar },
  { to: "/favorites", label: "收藏消息", icon: IconHeartOutline },
  { to: "/images", label: "对话图库", icon: IconImage },
] as const;

/** 同一笔每日赠送只在这个浏览器里提示一次，刷新或切换账号都不重复飘。 */
const DAILY_GRANT_SEEN_KEY = "dreamverse.daily-grant-seen";
const DAILY_GRANT_SEEN_LIMIT = 64;
const dailyGrantSeenInMemory = new Set<string>();

/**
 * 记录一笔已经展示过的每日赠送。
 *
 * 旧实现只保存“最后一笔 id”：A 账号 → B 账号 → A 账号时，B 的 id 会覆盖 A，
 * 回到 A 就会把当天同一笔 +20 再提示一次。这里改成有界 id 集合；兼容旧版留下的
 * 单个 id，并用内存集合兜住禁用 localStorage 的宿主。
 */
function markDailyGrantSeen(transactionId: string): boolean {
  if (dailyGrantSeenInMemory.has(transactionId)) return false;

  let seen: string[] = [];
  try {
    const raw = localStorage.getItem(DAILY_GRANT_SEEN_KEY);
    if (raw) {
      try {
        const parsed: unknown = JSON.parse(raw);
        seen = Array.isArray(parsed)
          ? parsed.filter((id): id is string => typeof id === "string")
          : [raw];
      } catch {
        // 旧版本保存的是裸 transactionId，不是 JSON。
        seen = [raw];
      }
    }
  } catch {
    // 某些 WebView 禁用持久存储；本次页面生命周期仍由内存集合去重。
  }

  for (const id of seen) dailyGrantSeenInMemory.add(id);
  if (dailyGrantSeenInMemory.has(transactionId)) return false;

  dailyGrantSeenInMemory.add(transactionId);
  try {
    localStorage.setItem(
      DAILY_GRANT_SEEN_KEY,
      JSON.stringify([transactionId, ...seen.filter((id) => id !== transactionId)].slice(0, DAILY_GRANT_SEEN_LIMIT)),
    );
  } catch {
    // 内存集合已经记住，不让存储异常影响聊天页。
  }
  return true;
}

/**
 * 关系数值读不到时的形态。
 *
 * 全部为 null。**不要在这里填默认值**——0 会被读成「我被扣光了」，
 * 1 会被读成「我还是 1 级」，两者都是在替服务端编数字。
 */
const UNKNOWN_RELATION: RelationSnapshot = {
  level: null,
  levelProgress: null,
  bond: null,
  bondForThisLevel: null,
  bondToNextLevel: null,
  affinity: null,
  stageLabel: null,
  power: null,
  powerProgress: null,
};

/**
 * 服务端下发的关系 → HUD 快照。
 *
 * `started === false` 时同样回落成全 null：那是「还没开始」，
 * 不是「等级 1、好感 0、灵力 0」。把零值画出来会让一个刚注册的人
 * 看到「灵力微弱」，以为自己已经把额度用完了。
 */
const toSnapshot = (view: RelationshipView): RelationSnapshot =>
  view.started
    ? {
        level: view.level,
        levelProgress: view.levelProgress,
        bond: view.bond,
        bondForThisLevel: view.bondForThisLevel,
        bondToNextLevel: view.bondToNextLevel,
        affinity: view.affinity,
        stageLabel: view.stageLabel,
        power: view.powerBalance,
        powerProgress: view.powerProgress,
      }
    : UNKNOWN_RELATION;

/** 一个数都没有就不画那块面板：满屏横杠不如干脆不出现。 */
const hasAnyValue = (r: RelationSnapshot) =>
  r.level !== null || r.affinity !== null || r.power !== null;

export function AppShell() {
  const session = useSession();
  // 在挂载聊天外壳之前路由，避免查询页触发关系读取、赠送提示或聊天分享卡。
  if (session.knowledgeQaHome) return <Navigate to="/information" replace />;
  return <CharacterAppShell />;
}

function CharacterAppShell() {
  const session = useSession();
  const appearance = useAppearance();
  const location = useLocation();
  const navigate = useNavigate();
  const [powerSheet, setPowerSheet] = useState(false);
  const [levelSheet, setLevelSheet] = useState(false);
  const [stagePortrait, setStagePortrait] = useState<StagePortraitOverride | null>(null);
  const [pendingTopupThankOrderIds, setPendingTopupThankOrderIds] = useState<string[]>([]);
  const { showValueChanges } = useFloatingToast();

  const [mode, setMode] = useState<ChatMode>(() =>
    localStorage.getItem(MODE_KEY) === "bubble" ? "bubble" : "theater",
  );
  useEffect(() => {
    localStorage.setItem(MODE_KEY, mode);
  }, [mode]);

  const toggleMode = useCallback(
    () => setMode((m) => (m === "theater" ? "bubble" : "theater")),
    [],
  );
  const openPowerSheet = useCallback(() => setPowerSheet(true), []);

  const characterChatPage = location.pathname === "/" || location.pathname === "/chat";
  const secondary = !characterChatPage;

  /*
   * 关系数值取一次，HUD 与 `/relationship` 共用。
   *
   * 跟着 `session.status` 重取：登录成功后必须刷新，否则用户登进来看到的还是
   * 登录前那次 401 的失败态。退出登录时整棵树卸载，不需要在这里清。
   */
  const relationship = useResource<RelationshipView>(
    (signal) => fetchRelationship(signal),
    [session.status],
  );

  useEffect(() => {
    const refresh = () => relationship.refresh();
    window.addEventListener(RELATIONSHIP_CHANGED_EVENT, refresh);
    return () => window.removeEventListener(RELATIONSHIP_CHANGED_EVENT, refresh);
  }, [relationship.refresh]);

  const completeTopupThank = useCallback((orderId: string) => {
    setPendingTopupThankOrderIds((current) => current.filter((id) => id !== orderId));
  }, []);

  const handlePowerPurchaseSucceeded = useCallback(
    (result: {
      orderId: string;
      grantedPower: number;
      grantedBond: number;
      grantedAffinity: number;
    }) => {
      setPowerSheet(false);
      relationship.reload();
      showValueChanges([
        { label: "灵力", delta: result.grantedPower },
        { label: "羁绊", delta: result.grantedBond },
        { label: "好感", delta: result.grantedAffinity },
      ]);
      setPendingTopupThankOrderIds((current) =>
        current.includes(result.orderId) ? current : [...current, result.orderId],
      );
      // 知识问答成为首页时，购买后的角色致谢仍必须回到原角色聊天消费；
      // 否则待致谢订单会滞留在外壳里，直到用户手动切换页面。
      const chatPath = session.knowledgeQaHome ? "/chat" : "/";
      if (location.pathname !== chatPath) navigate(chatPath, { replace: true });
    },
    [
      location.pathname,
      navigate,
      relationship.reload,
      session.knowledgeQaHome,
      showValueChanges,
    ],
  );

  /*
   * 聊天完成后的关系刷新是后台校准，不应让 HUD 在请求期间整块消失。
   *
   * useResource.reload() 会暂时回到 loading；如果直接把它折成 UNKNOWN_RELATION，
   * Stage 测得的 --hud-h 就会经历 90px → 0 → 90px，气泡消息区也随之上下伸缩。
   * 数值飘窗恰好与这次刷新同时出现，所以用户看到的是「飘窗一出，对话跳一下」。
   * 首次加载仍然没有缓存，不会凭空画一份假数据；刷新失败也会照旧撤掉 HUD。
   */
  const lastReadyRelationship = useRef<RelationshipView | null>(null);
  if (relationship.state.phase === "ready") {
    lastReadyRelationship.current = relationship.state.data;
  }
  const visibleRelationship =
    relationship.state.phase === "ready"
      ? relationship.state.data
      : relationship.state.phase === "loading"
        ? lastReadyRelationship.current
        : null;

  /*
   * 每日赠送由聊天入口的 newDay 打招呼分支在服务端幂等发放，流水是到账事实。
   *
   * React StrictMode 会把首次请求跑两遍：若只看「是不是这次请求发的」，第一份
   * 响应可能已被取消，第二份又只会得到“已发过”，飘窗便永远不出现。这里认流水
   * id，并在 localStorage 记住已经提示过的那一笔，既扛并发也不因刷新重复提示。
   */
  useEffect(() => {
    if (relationship.state.phase !== "ready") return;
    const dailyGrant = relationship.state.data.ledger.find(
      (entry) => entry.reasonKey === "daily_grant" && entry.delta > 0,
    );
    if (!dailyGrant || !markDailyGrantSeen(dailyGrant.id)) return;
    showValueChanges([{ label: "灵力", delta: dailyGrant.delta }]);
  }, [relationship.state, showValueChanges]);

  const relation = visibleRelationship
    ? toSnapshot(visibleRelationship)
    : UNKNOWN_RELATION;

  /*
   * 取不到用户信息就不画右上角那一排。
   *
   * 这排五个入口里，回忆点滴 / 收藏消息 / 对话图库 / 我的
   * 点进去都依赖用户与角色上下文，取不到时摆一排点了没用的按钮更糟。
   * 头像还是本地兜底图——摆一排点了没用的按钮比不摆更糟。
   *
   * 判据用 session.appearance 而不是 useAppearance().fromPreview：后者在
   * 线上环境关闭离线预览时恒为 false，生产环境会把「没有数据」误判成
   * 「有数据」。appearance 只可能由服务端下发写入，是可靠的信号。
   *
   * ⚠ 两个代价，都认了：
   *   - 离线时切不了对话模式。那只是本机偏好，存在 localStorage 里，
   *     连上之后照旧，切不了不影响任何数据。
   *   - 离线时进不去设置页，AI 生成内容告知就在那里。但离线时屏幕上
   *     没有任何模型生成的内容，告知义务本来就不触发。
   */
  const hasServerIdentity = session.appearance !== null;

  /*
   * 微信内的分享卡片。
   *
   * 不设的话微信自己抓，抓出来的是 `<title>` 加页面上第一段文字加第一张图——
   * 对一个 SPA 来说通常是「灵境」三个字配一张背景图，看不出这是什么。
   *
   * 依赖 appearance：卡片上要用角色的名字与头像，而它由服务端下发。
   * 拿不到就不设，让微信抓——那比设一张写着「加载中」的卡片好。
   *
   * 其它宿主里 `setShareCard` 内部会直接返回，这里不判断宿主
   * （`services/README.md`：feature 层不做 UA 判断）。
   */
  useEffect(() => {
    if (!appearance.avatarUrl) return;
    void setShareCard({
      title: `和${appearance.name}说说话`,
      desc: "灵境里有个梦灵在等你。",
      imgUrl: appearance.avatarUrl,
    });
  }, [appearance.name, appearance.avatarUrl]);

  const shellClass = [
    "app-shell",
    `app-shell--${session.host.formFactor}`,
    secondary ? "app-shell--secondary" : "",
  ]
    .filter(Boolean)
    .join(" ");
  const displayedPortrait = stagePortrait ?? {
    name: appearance.name,
    portraitUrl: appearance.portraitUrl,
    // 全站外观已带 weakened 前缀时，服务端图失败也不能退回普通缈缈。
    allowLocalFallback: appearance.prefix === "",
  };

  return (
    <div className={shellClass}>
      <DreamBackdrop
        url={appearance.backgroundUrl}
        fallbackUrl={LOCAL_ASSETS.background}
      />

      <div className="phone">
        <Stage
          hud={
            hasAnyValue(relation) ? (
              <ValueHud
                relation={relation}
                onExplainLevel={() => setLevelSheet(true)}
                onExplainPower={openPowerSheet}
              />
            ) : null
          }
          rail={
            hasServerIdentity ? (
              <>
                <nav className="rail" aria-label="次级入口">
                  {RAIL.map(({ to, label, icon: Icon }) => (
                    <NavLink
                      key={to}
                      to={to}
                      className={({ isActive }) =>
                        `rail__item${isActive ? " is-active" : ""}`
                      }
                      aria-label={label}
                      title={label}
                    >
                      <Icon size={18} />
                    </NavLink>
                  ))}
                {/* 旧版：interactionMode 为真时显示 CommentOutlined，否则 SolutionOutlined。 */}
                    <button
                      type="button"
                      className="rail__item"
                      onClick={toggleMode}
                      aria-label={
                        mode === "theater" ? "切换到气泡模式" : "切换到对话框模式"
                      }
                      title={
                        mode === "theater" ? "切换到气泡模式" : "切换到对话框模式"
                      }
                    >
                      {mode === "theater" ? (
                        <IconComment size={18} />
                      ) : (
                        <IconSolution size={18} />
                      )}
                    </button>

                {/*
                  这个入口指向「我的」，所以画的是**用户自己的**头像。

                  ⚠ 这里曾经画 `appearance.avatarUrl`，那是**角色**的头像——
                  一排入口里唯一属于用户的那个却顶着她的脸，等于把用户的位置
                  让了出去。拿不到就画占位图标，**不要回落到角色头像**，
                  也不要回落到离线预览那张：回落回去就等于把 bug 改成了默认行为。

                  实测 1,500 条 Profile 里只有 107 条有头像（旧系统迁来的微信
                  头像），所以占位图标是多数人看到的样子，不是异常分支。
                  用户端上传端点上线后这个比例才会变。
                */}
                  <NavLink
                    to="/profile"
                    className={({ isActive }) =>
                      `rail__item rail__item--avatar${isActive ? " is-active" : ""}`
                    }
                    aria-label="我的"
                    title="我的"
                  >
                    {session.accountAvatarUrl ? (
                      <img src={session.accountAvatarUrl} alt="" />
                    ) : (
                      <IconUser size={16} />
                    )}
                  </NavLink>
                </nav>
                <TodayChannelBubbles />
              </>
            ) : null
          }
          portrait={
              <StagePortrait
                state={
                  displayedPortrait.portraitUrl
                    ? {
                        kind: "ready",
                        url: displayedPortrait.portraitUrl,
                        alt: `${displayedPortrait.name}的立绘`,
                        ...(displayedPortrait.allowLocalFallback
                          ? { fallbackUrl: LOCAL_ASSETS.portrait }
                          : {}),
                      }
                    : { kind: "unavailable" }
                }
              />
          }
        >
          <Outlet
            context={{
              mode,
              relationship,
              setStagePortrait,
              pendingTopupThankOrderIds,
              completeTopupThank,
            } satisfies ChatModeContext}
          />
        </Stage>
      </div>

      <MusicPlayerPopover />

      <Sheet
        open={levelSheet}
        title="羁绊等级"
        onClose={() => setLevelSheet(false)}
      >
        <div className="prose">
          {relation.level === null ? (
            <p>现在读不到你们的羁绊，等连上灵境再来看看吧。</p>
          ) : relation.bondToNextLevel === null || relation.bondToNextLevel <= 0 ? (
            <p>
              你们已经是 <strong>{relation.level}</strong> 级，也是目前的最高等级啦。
            </p>
          ) : (
            <p>
              现在是 <strong>{relation.level}</strong> 级。距离{" "}
              <strong>{relation.level + 1}</strong> 级还需{" "}
              <strong>{relation.bondToNextLevel}</strong> 点羁绊
              {relation.bond !== null && relation.bondForThisLevel !== null
                ? `（本级已攒 ${relation.bond}/${relation.bondForThisLevel}）`
                : ""}
              。
            </p>
          )}
          <p>
            羁绊是聊天一点点攒起来的，只会多不会少。
            <strong>等级到手就不会掉</strong>——那是你花的时间。
          </p>
        </div>
      </Sheet>

      <PowerTopupSheet
        open={powerSheet}
        onOpen={openPowerSheet}
        onClose={() => setPowerSheet(false)}
        onSucceeded={handlePowerPurchaseSucceeded}
      />
    </div>
  );
}
